import React, { useEffect, useState } from 'react';
import {
  Typography,
  Card,
  CardContent,
  Box,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Chip,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import {
  Settings as SettingsIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { ApplicationInfo, PermissionUtils } from './types';

interface GeneralSettingsProps {
  applicationInfo: ApplicationInfo;
  setApplicationInfo: React.Dispatch<React.SetStateAction<ApplicationInfo>>;
  isEditing: boolean;
  setIsEditing: (editing: boolean) => void;
  isLoading: boolean;
  showSuccessAlert: boolean;
  appStatuses: string[];
  onSave: () => Promise<void>;
  onDeleteApplication: () => void;
  permissionUtils: PermissionUtils;
}

const GeneralSettings: React.FC<GeneralSettingsProps> = ({
  applicationInfo,
  setApplicationInfo,
  isEditing,
  setIsEditing,
  isLoading,
  showSuccessAlert,
  appStatuses,
  onSave,
  onDeleteApplication,
  permissionUtils
}) => {
  const { canEditSection } = permissionUtils;
  const [editingInfo, setEditingInfo] = useState(false);
  const [editingFeatures, setEditingFeatures] = useState(false);

  useEffect(() => {
    if (!isEditing) {
      setEditingInfo(false);
      setEditingFeatures(false);
    }
  }, [isEditing]);

  const handleApplicationInfoChange = (field: keyof ApplicationInfo) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setApplicationInfo(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const startSectionEdit = (section: 'info' | 'features') => {
    if (section === 'info') {
      setEditingInfo(true);
    } else {
      setEditingFeatures(true);
    }
    setIsEditing(true);
  };

  const cancelSectionEdit = (section: 'info' | 'features') => {
    const nextInfo = section === 'info' ? false : editingInfo;
    const nextFeatures = section === 'features' ? false : editingFeatures;
    setEditingInfo(nextInfo);
    setEditingFeatures(nextFeatures);
    if (!nextInfo && !nextFeatures) {
      setIsEditing(false);
    }
  };

  const renderSectionActions = (section: 'info' | 'features') => {
    if (!canEditSection('general')) {
      return null;
    }

    const isSectionEditing = section === 'info' ? editingInfo : editingFeatures;

    return (
      <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
        {isSectionEditing ? (
          <>
            <Tooltip title={isLoading ? 'Saving...' : 'Save'}>
              <span>
                <IconButton
                  color="primary"
                  onClick={onSave}
                  disabled={isLoading}
                  size="small"
                  aria-label={`Save ${section === 'info' ? 'application information' : 'feature configuration'}`}
                >
                  <SaveIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Cancel">
              <span>
                <IconButton
                  onClick={() => cancelSectionEdit(section)}
                  disabled={isLoading}
                  size="small"
                  aria-label={`Cancel ${section === 'info' ? 'application information' : 'feature configuration'} edit`}
                >
                  <CancelIcon />
                </IconButton>
              </span>
            </Tooltip>
          </>
        ) : (
          <Tooltip title="Edit">
            <IconButton
              color="primary"
              onClick={() => startSectionEdit(section)}
              size="small"
              aria-label={`Edit ${section === 'info' ? 'application information' : 'feature configuration'}`}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  };

  return (
    <Grid2 container spacing={1}>
      <Grid2 size={{ xs: 12 }}>
        {showSuccessAlert && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Application settings have been updated successfully!
          </Alert>
        )}
      </Grid2>

      <Grid2 size={{ xs: 12, md: 8 }}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <SettingsIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Application Information</Typography>
              {renderSectionActions('info')}
            </Box>

            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Application ID"
                  value={applicationInfo.app_id}
                  disabled
                  variant="outlined"
                  sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace' } }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth disabled={!editingInfo || !canEditSection('general')}>
                  <InputLabel>Application Status</InputLabel>
                  <Select
                    value={applicationInfo.app_status}
                    onChange={(e) => setApplicationInfo(prev => ({ ...prev, app_status: e.target.value }))}
                    label="Application Status"
                  >
                    {appStatuses.map((status) => (
                      <MenuItem key={status} value={status}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: status === 'Active' ? 'success.main' : 'error.main',
                              mr: 1
                            }}
                          />
                          {status}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Application Name"
                  value={applicationInfo.name}
                  onChange={handleApplicationInfoChange('name')}
                  disabled={!editingInfo || !canEditSection('general')}
                  variant="outlined"
                />
              </Grid2>
            </Grid2>

            <Divider sx={{ my: 3 }} />

            {/* Feature Toggles Section */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <SettingsIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Feature Configuration</Typography>
                {renderSectionActions('features')}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enable or disable application features
              </Typography>

              <Grid2 container spacing={2}>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={applicationInfo.is_calls_enabled}
                        onChange={(e) => setApplicationInfo(prev => ({ ...prev, is_calls_enabled: e.target.checked }))}
                        disabled={!editingFeatures || !canEditSection('general')}
                        color="primary"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Enable Voice/Video Calls
                        </Typography>
                      </Box>
                    }
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={applicationInfo.is_chats_enabled}
                        onChange={(e) => setApplicationInfo(prev => ({ ...prev, is_chats_enabled: e.target.checked }))}
                        disabled={!editingFeatures || !canEditSection('general')}
                        color="primary"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Enable Chat Messaging
                        </Typography>
                      </Box>
                    }
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={applicationInfo.is_chat_bot_enabled}
                        onChange={(e) => setApplicationInfo(prev => ({ ...prev, is_chat_bot_enabled: e.target.checked }))}
                        disabled={!editingFeatures || !canEditSection('general')}
                        color="primary"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Enable Chat Bot Service
                        </Typography>
                      </Box>
                    }
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={applicationInfo.is_notifications_enabled}
                        onChange={(e) => setApplicationInfo(prev => ({ ...prev, is_notifications_enabled: e.target.checked }))}
                        disabled={!editingFeatures || !canEditSection('general')}
                        color="primary"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Enable Push Notifications
                        </Typography>
                      </Box>
                    }
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={applicationInfo.is_scheduled_calls_enabled}
                        onChange={(e) => setApplicationInfo(prev => ({ ...prev, is_scheduled_calls_enabled: e.target.checked }))}
                        disabled={!editingFeatures || !canEditSection('general')}
                        color="primary"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Enable Scheduled Calls
                        </Typography>
                      </Box>
                    }
                  />
                </Grid2>
              </Grid2>
            </Box>
          </CardContent>
        </Card>
      </Grid2>

      {/* Application Overview Card - moved here */}
      <Grid2 size={{ xs: 12, md: 4 }}>
        <Card sx={{ mb: 2, height: 'fit-content' }}>
          <CardContent sx={{
            display: 'flex',
            flexDirection: 'column',
            '&:last-child': { pb: 2 }
          }}>
            <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 1 }}>
              Application Overview
            </Typography>
            <Stack spacing={1.5}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">Status:</Typography>
                <Chip
                  label={applicationInfo.app_status}
                  color={applicationInfo.app_status === 'Active' ? 'success' : 'error'}
                  size="small"
                />
              </Box>
              <Divider />
              <Typography variant="subtitle2" color="primary" sx={{ fontSize: '0.85rem' }}>Feature Status</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>Voice/Video Calls:</Typography>
                <Chip
                  label={applicationInfo.is_calls_enabled ? 'Enabled' : 'Disabled'}
                  color={applicationInfo.is_calls_enabled ? 'success' : 'default'}
                  size="small"
                  variant="outlined"
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>Chat Messaging:</Typography>
                <Chip
                  label={applicationInfo.is_chats_enabled ? 'Enabled' : 'Disabled'}
                  color={applicationInfo.is_chats_enabled ? 'success' : 'default'}
                  size="small"
                  variant="outlined"
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>Chat Bot Service:</Typography>
                <Chip
                  label={applicationInfo.is_chat_bot_enabled ? 'Enabled' : 'Disabled'}
                  color={applicationInfo.is_chat_bot_enabled ? 'success' : 'default'}
                  size="small"
                  variant="outlined"
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>Push Notifications:</Typography>
                <Chip
                  label={applicationInfo.is_notifications_enabled ? 'Enabled' : 'Disabled'}
                  color={applicationInfo.is_notifications_enabled ? 'success' : 'default'}
                  size="small"
                  variant="outlined"
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>Scheduled Calls:</Typography>
                <Chip
                  label={applicationInfo.is_scheduled_calls_enabled ? 'Enabled' : 'Disabled'}
                  color={applicationInfo.is_scheduled_calls_enabled ? 'success' : 'default'}
                  size="small"
                  variant="outlined"
                />
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Delete Application Card - moved here and only show if user has permission */}
        {permissionUtils.canPerformAction('deleteApplication') && (
          <Card sx={{ mt: 2, height: 'fit-content' }}>
            <CardContent sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              '&:last-child': { pb: 2 }
            }}>
              <Typography variant="h6" gutterBottom color="error" sx={{ mb: 1 }}>
                Danger Zone
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flex: 1 }}>
                Once you delete an application, there is no going back. Please be certain.
              </Typography>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                fullWidth
                onClick={onDeleteApplication}
                sx={{ mt: 'auto' }}
              >
                Delete Application
              </Button>
            </CardContent>
          </Card>
        )}
      </Grid2>
    </Grid2>
  );
};

export default GeneralSettings;